/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { defaultTo, get, pickBy, pick, isEmpty } from 'lodash';
import { useContext } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Field, Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { MeContext } from 'blb-contexts/Me';
import { updateUserAddress } from 'blb-api/users';
import { errorMessage } from 'blb-utils/errors';
import * as Sentry from '@sentry/browser';

import { Modal, ActionButton, AlertBar } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { ModalContent } from 'blb-components/ModalContent';
import {
  typeInputConfig,
  lineTextInputConfig,
  cityInputConfig,
  countyInputConfig,
  stateInputConfig,
  zipcodeInputConfig,
  isDefaultAddressInputConfig,
  additionalLineTextInputConfig,
} from 'blb-inputs/common';

const styles = StyleSheet.create({
  checkBoxInputField: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  fieldWrapper: {
    flexBasis: '48%',
  },
  footerBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 28,
  },
  row: {
    ':not(:last-child)': {
      marginBottom: 24,
    },
    display: 'flex',
    justifyContent: 'space-between',
  },
  rowSingle: {
    ':not(:last-child)': {
      marginBottom: 24,
    },
    display: 'flex',
  },
  spacer: {
    ':not(:last-child)': {
      marginBottom: 24,
    },
  },
});

export namespace AddressModal {
  export interface Props {
    address?: BlendBridge.API.Address;
    fetchUserInfo: () => Promise<void>;
    handleClose: () => void;
    headerText: string;
    isOpen: boolean;
  }
}

type FormValues = BlendBridge.API.Address;
const addressFields: (keyof BlendBridge.API.Address)[] = [
  'additionalLineText',
  'city',
  'county',
  'id',
  'isDefault',
  'lineText',
  'state',
  'type',
  'zipcode',
];

export const AddressModal: React.FC<AddressModal.Props> = ({
  isOpen,
  handleClose,
  headerText,
  fetchUserInfo,
  address,
}) => {
  const existingAddressFields = address && !isEmpty(address) && pick(address, addressFields);
  const initialValues = defaultTo(existingAddressFields, {
    additionalLineText: '',
    city: '',
    isDefault: false,
    lineText: '',
    state: '',
    type: 'WORK',
    zipcode: '',
  });
  const me = useContext(MeContext);
  const onSubmit = async (formValues: FormValues) => {
    try {
      await updateUserAddress(me!.principalId, pickBy(formValues));
      await fetchUserInfo();
      handleClose();
    } catch (error) {
      Sentry.captureException(error);
      return {
        [FORM_ERROR]: get(error, 'response.data', errorMessage('SAVING_ADDRESS', me?.principalType)),
      };
    }
  };
  return (
    <Modal contentLabel={headerText} handleClose={handleClose} isOpen={isOpen}>
      <ModalContent handleClose={handleClose} headerText={headerText}>
        <Form initialValues={initialValues} onSubmit={onSubmit}>
          {({ handleSubmit, submitError, submitting }) => {
            return (
              <div>
                {submitError && (
                  <div className={css(styles.spacer)}>
                    <AlertBar id={`UserProfile-address-error`} message={submitError} theme="error" />
                  </div>
                )}
                <div className={css(styles.row)}>
                  <div className={css(styles.fieldWrapper)}>
                    <Field {...typeInputConfig} />
                  </div>
                  <div className={css(styles.fieldWrapper, styles.checkBoxInputField)}>
                    <Field {...isDefaultAddressInputConfig} />
                  </div>
                </div>
                <div className={css(styles.row)}>
                  <div className={css(styles.fieldWrapper)}>
                    <Field {...lineTextInputConfig} />
                  </div>
                  <div className={css(styles.fieldWrapper)}>
                    <Field {...additionalLineTextInputConfig} />
                  </div>
                </div>
                <div className={css(styles.row)}>
                  <div className={css(styles.fieldWrapper)}>
                    <Field {...cityInputConfig} />
                  </div>
                  <div className={css(styles.fieldWrapper)}>
                    <Field {...countyInputConfig} />
                  </div>
                </div>
                <div className={css(styles.row)}>
                  <div className={css(styles.fieldWrapper)}>
                    <Field {...stateInputConfig} />
                  </div>
                  <div className={css(styles.fieldWrapper)}>
                    <Field {...zipcodeInputConfig} />
                  </div>
                </div>
                <div className={css(styles.footerBar)}>
                  <ActionButton
                    id="AddressModal-submit"
                    isLoading={submitting}
                    label="Save changes"
                    onClick={handleSubmit}
                  />
                </div>
              </div>
            );
          }}
        </Form>
      </ModalContent>
    </Modal>
  );
};
